import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import gsap from 'gsap';

// Confetti particle system using GSAP
export function Confetti({ trigger, duration = 3 }) {
  const containerRef = useRef(null);
  const tlRef = useRef(null);

  useEffect(() => {
    if (!trigger || !containerRef.current) return;

    const container = containerRef.current;
    container.innerHTML = '';

    const colors = ['#00E5FF', '#FF3366', '#FFC700', '#00FF66', '#FF6B35', '#A855F7'];
    const shapes = ['circle', 'rect', 'triangle'];
    const count = 80;

    for (let i = 0; i < count; i++) {
      const el = document.createElement('div');
      const color = colors[Math.floor(Math.random() * colors.length)];
      const shape = shapes[Math.floor(Math.random() * shapes.length)];
      const size = 6 + Math.random() * 10;

      el.style.cssText = `
        position: absolute;
        width: ${size}px;
        height: ${shape === 'rect' ? size * 2 : size}px;
        background: ${color};
        border-radius: ${shape === 'circle' ? '50%' : shape === 'triangle' ? '0' : '2px'};
        left: 50%;
        top: 40%;
        opacity: 1;
        pointer-events: none;
        ${shape === 'triangle' ? `
          width: 0; height: 0;
          background: transparent;
          border-left: ${size/2}px solid transparent;
          border-right: ${size/2}px solid transparent;
          border-bottom: ${size}px solid ${color};
        ` : ''}
      `;
      container.appendChild(el);

      gsap.fromTo(el, {
        x: 0, y: 0, rotation: 0, scale: 0,
      }, {
        x: (Math.random() - 0.5) * window.innerWidth * 0.8,
        y: (Math.random() - 0.7) * window.innerHeight * 0.9,
        rotation: Math.random() * 720 - 360,
        scale: 1,
        duration: 0.6 + Math.random() * 0.4,
        ease: 'power2.out',
        delay: Math.random() * 0.3,
      });

      gsap.to(el, {
        y: `+=${200 + Math.random() * 300}`,
        opacity: 0,
        rotation: `+=${Math.random() * 360}`,
        duration: 1.5 + Math.random(),
        delay: 0.8 + Math.random() * 0.5,
        ease: 'power1.in',
      });
    }

    return () => { container.innerHTML = ''; };
  }, [trigger]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 10000, overflow: 'hidden',
      }}
    />
  );
}

// Score pop animation hook
export function useScorePop(ref) {
  return useCallback((value) => {
    if (!ref.current) return;
    gsap.fromTo(ref.current, 
      { scale: 1.5, color: '#00FF66' },
      { scale: 1, color: '', duration: 0.4, ease: 'elastic.out(1, 0.5)' }
    );
  }, [ref]);
}

// Shake animation
export function shakeElement(element) {
  if (!element) return;
  gsap.fromTo(element,
    { x: 0 },
    {
      x: 12,
      duration: 0.08,
      repeat: 5,
      yoyo: true,
      ease: 'power2.inOut',
      onComplete: () => gsap.set(element, { x: 0 }),
    }
  );
}

// Flash correct (green pulse)
export function flashCorrect(element) {
  if (!element) return;
  gsap.timeline()
    .to(element, { 
      backgroundColor: '#00FF66', 
      color: '#050814', 
      borderColor: '#00FF66',
      scale: 1.05, 
      duration: 0.15, 
      ease: 'power2.out' 
    })
    .to(element, { 
      scale: 1, 
      duration: 0.3, 
      ease: 'elastic.out(1, 0.6)' 
    }, '+=0.1');
}

// Flash wrong (red shake)
export function flashWrong(element) {
  if (!element) return;
  gsap.timeline()
    .to(element, {
      backgroundColor: '#FF3366',
      color: '#FFFFFF',
      borderColor: '#FF3366',
      duration: 0.1,
    })
    .to(element, {
      x: 10, duration: 0.05, repeat: 5, yoyo: true,
      ease: 'power2.inOut',
    }, '<')
    .to(element, {
      backgroundColor: '',
      color: '',
      borderColor: '',
      x: 0,
      duration: 0.3,
    }, '+=0.1');
}

// Stagger entrance for list items
export function staggerIn(elements, fromDirection = 'bottom') {
  if (!elements || elements.length === 0) return;
  const from = {
    bottom: { y: 40, opacity: 0 },
    left: { x: -40, opacity: 0 },
    right: { x: 40, opacity: 0 },
    scale: { scale: 0.8, opacity: 0 },
  };
  gsap.fromTo(elements, from[fromDirection] || from.bottom, {
    y: 0, x: 0, opacity: 1, scale: 1,
    duration: 0.5,
    stagger: 0.06,
    ease: 'power3.out',
  });
}

// Race advance animation
export function raceAdvance(trackElement, toPercent) {
  if (!trackElement) return;
  gsap.to(trackElement, {
    width: `${toPercent}%`,
    duration: 0.5,
    ease: 'back.out(1.7)',
  });
}

// Tower block drop
export function blockDrop(blockElement, onComplete) {
  if (!blockElement) return;
  gsap.fromTo(blockElement,
    { y: -80, opacity: 0, scaleX: 1.2 },
    {
      y: 0, opacity: 1, scaleX: 1,
      duration: 0.4,
      ease: 'bounce.out',
      onComplete,
    }
  );
}

// Tower wobble
export function towerWobble(towerElement) {
  if (!towerElement) return;
  gsap.timeline()
    .to(towerElement, { rotation: -4, duration: 0.1, ease: 'power2.out' })
    .to(towerElement, { rotation: 3, duration: 0.1 })
    .to(towerElement, { rotation: -2, duration: 0.1 })
    .to(towerElement, { rotation: 1, duration: 0.1 })
    .to(towerElement, { rotation: 0, duration: 0.2, ease: 'elastic.out(1, 0.5)' });
}

// Tower collapse
export function towerCollapse(towerElement, onComplete) {
  if (!towerElement) return;
  const blocks = towerElement.children;
  gsap.timeline({ onComplete })
    .to(towerElement, { rotation: 8, duration: 0.3, ease: 'power2.in' })
    .to(Array.from(blocks), {
      y: () => 200 + Math.random() * 300,
      x: () => (Math.random() - 0.5) * 400,
      rotation: () => Math.random() * 360 - 180,
      opacity: 0,
      duration: 0.8,
      stagger: 0.03,
      ease: 'power2.in',
    }, '<+=0.1')
    .set(towerElement, { rotation: 0 });
}

// Card flip with GSAP
export function flipCard(cardElement, onHalfway) {
  if (!cardElement) return;
  gsap.timeline()
    .to(cardElement, {
      rotateY: 90,
      duration: 0.2,
      ease: 'power2.in',
      onComplete: onHalfway,
    })
    .to(cardElement, {
      rotateY: 180,
      duration: 0.2,
      ease: 'power2.out',
    });
}

// Card match celebration
export function cardMatchCelebrate(card1, card2) {
  [card1, card2].forEach(el => {
    if (!el) return;
    gsap.timeline()
      .to(el, { scale: 1.1, duration: 0.15, ease: 'power2.out' })
      .to(el, { 
        boxShadow: '0 0 25px rgba(0, 255, 102, 0.6)', 
        borderColor: '#00FF66',
        duration: 0.3 
      }, '<')
      .to(el, { scale: 1, duration: 0.3, ease: 'elastic.out(1, 0.6)' });
  });
}

// Pulse animation for timers
export function pulseElement(element) {
  if (!element) return;
  gsap.to(element, {
    scale: 1.15,
    duration: 0.3,
    yoyo: true,
    repeat: 1,
    ease: 'power2.inOut',
  });
}

// Number counter animation
export function animateNumber(element, from, to, duration = 0.5) {
  if (!element) return;
  const obj = { val: from };
  gsap.to(obj, {
    val: to,
    duration,
    ease: 'power2.out',
    onUpdate: () => {
      element.textContent = Math.round(obj.val);
    },
  });
  gsap.fromTo(element,
    { scale: 1.4 },
    { scale: 1, duration: 0.4, ease: 'elastic.out(1, 0.5)' }
  );
}
